<script setup lang="ts">
	import { computed, ref, watch } from 'vue'

	import { CircleStencil, Cropper } from 'vue-advanced-cropper'
	import 'vue-advanced-cropper/dist/style.css'

	interface Props {
		imageSrc: string
		isVisible: boolean
		cropMode?: 'square' | 'horizontal' | 'vertical' | 'free'
		aspectRatioLocked?: boolean
		stencilShape?: 'rectangle' | 'circle'
		autoUpload?: boolean
		uploading?: boolean
		uploadFunction?: (file: File) => Promise<void>
		/**
		 * Aplana transparencia a blanco al exportar. Pensado para logos que se
		 * muestran en tarjetas blancas (empresa, marca, hotel): sin esto, un PNG
		 * con fondo transparente se ve negro en el propio editor y puede quedar
		 * ilegible en cualquier UI que no controle su fondo. No usar en recortes
		 * circulares (avatares), que sí necesitan la transparencia.
		 */
		flattenToWhite?: boolean
	}

	interface Emits {
		(e: 'crop-applied', file: File): void
		(e: 'crop-cancelled'): void
		(e: 'update:isVisible', visible: boolean): void
		(e: 'update:uploading', uploading: boolean): void
	}

	const props = withDefaults(defineProps<Props>(), {
		cropMode: 'square',
		aspectRatioLocked: true,
		stencilShape: 'rectangle',
		autoUpload: false,
		uploading: false,
		flattenToWhite: false,
	})

	const emit = defineEmits<Emits>()

	const cropperRef = ref<InstanceType<typeof Cropper> | null>(null)
	const currentCropMode = ref(props.cropMode)
	const aspectRatioLocked = ref(props.aspectRatioLocked)

	const cropModes = ref([
		{ value: 'square', label: 'Cuadrado', iconPath: 'M4 4h16v16H4V4z' },
		{ value: 'horizontal', label: 'Horizontal', iconPath: 'M3 8h18v8H3V8z' },
		{ value: 'vertical', label: 'Vertical', iconPath: 'M8 3h8v18H8V3z' },
		{
			value: 'free',
			label: 'Libre',
			iconPath: 'M3 3h6v6H3V3zm12 0h6v6h-6V3zM3 15h6v6H3v-6zm12 0h6v6h-6v-6z',
		},
	])

	const modeAspectRatio: Record<string, number> = {
		square: 1,
		horizontal: 2,
		vertical: 0.5,
	}

	// Relación de aspecto forzada según el modo; null = recorte libre
	const stencilAspectRatio = computed(() => {
		if (!aspectRatioLocked.value) return undefined
		return modeAspectRatio[currentCropMode.value]
	})

	const setCropMode = (mode: 'square' | 'horizontal' | 'vertical' | 'free') => {
		currentCropMode.value = mode
		aspectRatioLocked.value = mode !== 'free'
	}

	const MAX_CANVAS_SIZE = 512

	// PNG solo si el recorte tiene transparencia real (logos); JPEG para fotos, más liviano
	const canvasHasTransparency = (canvas: HTMLCanvasElement): boolean => {
		const ctx = canvas.getContext('2d')
		if (!ctx) return false
		const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)
		for (let i = 3; i < data.length; i += 4) {
			if (data[i] < 255) return true
		}
		return false
	}

	const canvasToFile = (
		canvas: HTMLCanvasElement,
		fileName: string
	): Promise<File> => {
		const usePng = canvasHasTransparency(canvas)
		const mimeType = usePng ? 'image/png' : 'image/jpeg'
		const extension = usePng ? 'png' : 'jpg'

		return new Promise((resolve, reject) => {
			canvas.toBlob(
				(blob) => {
					if (blob) {
						resolve(
							new File([blob], `${fileName}.${extension}`, { type: mimeType })
						)
					} else {
						reject(new Error('No se pudo generar la imagen recortada'))
					}
				},
				mimeType,
				usePng ? undefined : 0.9
			)
		})
	}

	// Compone el canvas recortado sobre un fondo blanco opaco, para logos que
	// se muestran en tarjetas blancas y no deben depender de transparencia.
	const flattenOnWhite = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
		const flattened = document.createElement('canvas')
		flattened.width = canvas.width
		flattened.height = canvas.height
		const ctx = flattened.getContext('2d')
		if (!ctx) return canvas
		ctx.fillStyle = '#ffffff'
		ctx.fillRect(0, 0, flattened.width, flattened.height)
		ctx.drawImage(canvas, 0, 0)
		return flattened
	}

	const applyCrop = async () => {
		const result = cropperRef.value?.getResult()
		if (!result?.canvas) return

		const outputCanvas =
			props.flattenToWhite ? flattenOnWhite(result.canvas) : result.canvas

		let croppedFile: File
		try {
			croppedFile = await canvasToFile(outputCanvas, 'cropped-image')
		} catch (error) {
			console.error('Error generating cropped image:', error)
			return
		}

		if (props.autoUpload && props.uploadFunction) {
			try {
				emit('update:uploading', true)
				await props.uploadFunction(croppedFile)
				emit('update:isVisible', false)
			} catch (error) {
				console.error('Error uploading image:', error)
			} finally {
				emit('update:uploading', false)
			}
		} else {
			emit('crop-applied', croppedFile)
		}
	}

	const cancelCrop = () => {
		emit('crop-cancelled')
		emit('update:isVisible', false)
	}

	watch(
		() => props.cropMode,
		(newMode) => {
			currentCropMode.value = newMode
			aspectRatioLocked.value = props.aspectRatioLocked
		}
	)
</script>

<template>
	<div
		v-if="isVisible"
		class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
		@click.self="cancelCrop"
	>
		<div
			class="bg-white rounded-xl p-4 sm:p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
			@click.stop
		>
			<!-- Header -->
			<div class="text-center mb-3 sm:mb-6">
				<h4 class="text-xl font-semibold text-gray-800 mb-2">
					Recortar Imagen
				</h4>
				<p class="text-sm text-gray-600">
					Arrastra el área de recorte para ajustar la imagen
				</p>
			</div>

			<!-- Crop Mode Selection -->
			<div class="flex flex-wrap justify-center gap-2 mb-3 sm:mb-6">
				<button
					type="button"
					v-for="mode in cropModes"
					:key="mode.value"
					@click="setCropMode(mode.value as any)"
					:class="[
						'px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 flex items-center gap-2',
						currentCropMode === mode.value ?
							'bg-blue-600 text-white shadow-md'
						:	'bg-gray-100 text-gray-600 hover:bg-gray-200',
					]"
				>
					<svg
						class="w-4 h-4"
						fill="currentColor"
						viewBox="0 0 24 24"
					>
						<path :d="mode.iconPath" />
					</svg>
					{{ mode.label }}
				</button>
			</div>

			<!-- Cropper -->
			<div class="bg-gray-50 rounded-lg p-4 shadow-inner">
				<Cropper
					ref="cropperRef"
					:src="imageSrc"
					class="cropper"
					:stencil-component="stencilShape === 'circle' ? CircleStencil : undefined"
					:stencil-props="{ aspectRatio: stencilAspectRatio }"
					:canvas="{ maxWidth: MAX_CANVAS_SIZE, maxHeight: MAX_CANVAS_SIZE }"
					image-restriction="stencil"
				/>
			</div>

			<!-- Controls -->
			<div class="flex flex-col sm:flex-row gap-3 justify-center mt-4 sm:mt-6">
				<button
					type="button"
					@click="cancelCrop"
					class="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
				>
					<svg
						class="w-4 h-4 inline mr-2"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M6 18L18 6M6 6l12 12"
						></path>
					</svg>
					Cancelar
				</button>

				<button
					type="button"
					@click="applyCrop"
					:disabled="uploading"
					:class="[
						'px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105',
						uploading ?
							'bg-gray-400 text-white cursor-not-allowed'
						:	'bg-blue-600 text-white hover:bg-blue-700',
					]"
				>
					<div
						v-if="uploading"
						class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
					></div>
					<svg
						v-else
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M5 13l4 4L19 7"
						></path>
					</svg>
					{{
						uploading ? 'Subiendo...'
						: autoUpload ? 'Aplicar y Subir'
						: 'Aplicar Recorte'
					}}
				</button>
			</div>
		</div>
	</div>
</template>

<style scoped>
	.cropper {
		/* min() en vez de 400px fijo: en celulares de poca altura, el modal completo
		   (header + botones de modo + cropper + controles) no cabía en max-h-[90vh],
		   forzaba scroll en el modal, y el arrastre del stencil competía con ese
		   scroll al recortar desde el teléfono. */
		height: min(400px, 45vh);
		width: 100%;
		background: #f3f4f6;
	}

	/* La librería pinta de negro el área fuera de la imagen por defecto; con
	   logos de fondo transparente eso se ve como si la imagen estuviera rota.
	   El overlay de atenuación (foreground, 50%) se deja tal cual. */
	.cropper :deep(.vue-advanced-cropper__background) {
		background: #ffffff;
	}
</style>
